import type { Capabilities, MeResponse, Membership, WorkspaceRef } from "@ondo/shared";

import { About } from "../about/About.tsx";
import { Activity } from "../activity/Activity.tsx";
import { BorrowedLoans } from "../loans/BorrowedLoans.tsx";
import { Profile } from "../profile/Profile.tsx";
import { NotBuiltYet } from "../shell/NotBuiltYet.tsx";
import { PortalShell } from "../shell/PortalShell.tsx";
import { Notifications } from "../notifications/Notifications.tsx";
import { NotificationPreferences } from "../notificationPreferences/NotificationPreferences.tsx";
import { atLeast } from "@ondo/shared";
import { workspacePath } from "../routes/routes.model.ts";
import { inboxPrincipalFor } from "../shell/shell.model.ts";
import { Verifications } from "../verifications/Verifications.tsx";
import { WorkRecord } from "../work/WorkRecord.tsx";
import { Contacts } from "./Contacts.tsx";
import { GalleryAgents } from "./GalleryAgents.tsx";
import { Inventory } from "./Inventory.tsx";
import { Reporting } from "./Reporting.tsx";
import { Settlements } from "./Settlements.tsx";
import { TransactionCentre } from "./TransactionCentre.tsx";
import { SoldWorks } from "./SoldWorks.tsx";
import { GALLERY_PORTAL } from "./gallery.constants.ts";
import type { GalleryDestination } from "./gallery.constants.ts";
import type { PortalView } from "../shell/shell.model.ts";
import "./gallery.css";

/**
 * The gallery's workspace.
 *
 * The frame is `PortalShell`, shared with the collector and the agent; what is
 * here is which component each destination renders — the one thing that
 * genuinely differs between portals.
 *
 * Five of its screens are other portals', unchanged and unwrapped. That is the
 * point rather than a shortcut: what a gallery may do to a work is decided in
 * SQL, so a second, gallery-shaped copy of the work record or the verification
 * inbox would be a second opinion about that. The same components render,
 * narrowed by the same policies.
 */
export function GalleryWorkspace({
  destination,
  record,
  workspace,
  principal,
  capabilities,
  membership,
  canSwitch,
  onSignOut,
}: {
  destination: GalleryDestination;
  /** Which work a `work` destination is about. Null for every other one. */
  record: string | null;
  workspace: WorkspaceRef;
  principal: MeResponse["principal"];
  capabilities: Capabilities;
  /** The caller's membership of this workspace's organisation, or null. */
  membership: Membership | null;
  canSwitch: boolean;
  onSignOut: () => void;
}) {
  return (
    <PortalShell
      spec={GALLERY_PORTAL}
      destination={destination}
      workspace={workspace}
      principal={principal}
      capabilities={capabilities}
      membership={membership}
      canSwitch={canSwitch}
      onSignOut={onSignOut}
    >
      {(view) => (
        <Destination
          destination={destination}
          record={record}
          workspace={workspace}
          capabilities={capabilities}
          membership={membership}
          principal={principal}
          view={view}
          onSignOut={onSignOut}
        />
      )}
    </PortalShell>
  );
}

/** The one place a switch over the gallery's destinations lives. */
function Destination({
  destination,
  record,
  workspace,
  capabilities,
  membership,
  principal,
  view,
  onSignOut,
}: {
  destination: GalleryDestination;
  record: string | null;
  workspace: WorkspaceRef;
  capabilities: Capabilities;
  /**
   * The caller's rung on this firm, or null on a personal workspace.
   *
   * Read ONLY through `atLeast`, and only ever to decline offering a control
   * the database will refuse. A capability answers "may this account do this at
   * all"; a rung answers "may this SEAT do it here", and the two are different
   * questions — `transferWork` is true for a registrar who may sell and false
   * for a director with no consignment.
   */
  membership: Membership | null;
  principal: MeResponse["principal"];
  view: PortalView;
  onSignOut: () => void;
}) {
  switch (destination) {
    // The capability is PASSED, not read here and not assumed. `transferWork` is
    // the server's own answer about this caller — a live accepted consignment
    // held at `editor` or above — and the screen disables rather than hides the
    // control when it is false, the way the work record below takes
    // `canAssignAgent` for the same reason.
    case "inventory":
      return (
        <Inventory
          workBase={view.hrefs.workBase}
          canSell={capabilities.transferWork}
          canAnswer={atLeast(membership?.level, "editor")}
        />
      );

    // The inventory's opposite number, and a SECOND read rather than a filter
    // over the first: a sale ends the consignment, so a sold work is not a
    // differently-flagged inventory row but a different table reached through a
    // different relation.
    case "sold":
      return <SoldWorks workBase={view.hrefs.workBase} />;

    // WHAT A COVENANT PRODUCED, which is not what a covenant SAYS — Sold Works
    // shows the term and this shows the money it turned into. A second read
    // rather than a filter over that one, and for the reason the read itself
    // exists: a settlement is a STRANGER's sale that this gallery's covenant
    // reaches into, and `transfers_read` does not admit the caller to it at all.
    case "settlements":
      // Admin on the covenant-holding firm, which is what `settle_resale`,
      // `answer_settlement` and `unlock_covenant` all require. `atLeast`
      // answers true for a personal workspace, where there is no rung and the
      // caller acts as themselves.
      return (
        <Settlements
          workBase={view.hrefs.workBase}
          canSettle={atLeast(membership?.level, "admin")}
        />
      );

    // THE FIRM'S OWN AGENTS, in both directions, and this is no longer the
    // agent portal's screen.
    //
    // It was, and the comment here used to explain why: "Agents" is ambiguous in
    // a gallery — mandates the firm HOLDS over other people's works, or agents
    // it has APPOINTED over the works it is selling — and only the first had a
    // relation behind it, "because a gallery owns no works to appoint anyone
    // over".
    //
    // That clause is still true and the conclusion drawn from it stopped being
    // so. 20260905090000 widened seven predicates from `owned_work_ids()` to
    // include `sellable_work_ids()`: a gallery owns nothing, and under a live
    // accepted consignment it may sell the picture, so it may appoint somebody
    // to show it. Both directions are `ondo.work_agent_mandates` read from
    // opposite ends, which is why this is one screen and not two.
    case "agents":
      return <GalleryAgents workBase={view.hrefs.workBase} />;

    // WHO, rather than what. Every other gallery screen is keyed on an object
    // and carries the counterparty as a column; this is the same rows pivoted
    // onto the person, from `ondo.my_contacts()`. It writes nothing and stores
    // nothing — a contact is not a record ONDO keeps, it is a record ONDO
    // already has, read from the other end.
    case "contacts":
      return (
        <Contacts
          workBase={view.hrefs.workBase}
          canNote={atLeast(membership?.level, "editor")}
          // WHICH SEAT IS READING, for the reader stamp — OND-67. The firm's
          // own id, from `/v1/me`, and the server refuses any id the caller
          // holds no seat in. It grants nothing: the rows are bounded by
          // `ondo.my_contacts()` exactly as they were.
          actingAs={workspace.organisation?.ondoId ?? null}
        />
      );

    // The FIRM's inbox on an organisation workspace, the reader's own on a
    // personal one — `inboxPrincipalFor`, exactly as Verifications above.
    //
    // `workspace` AND an id, which look redundant and are not: the workspace
    // decides where each row's action LINKS to, and the id decides whose feed
    // is read. On a firm workspace those are two different principals, and
    // conflating them is what an inbox showing a member's own empty feed under
    // the firm's heading would look like.
    case "notifications":
      return (
        <Notifications
          workspace={workspace}
          inboxOndoId={inboxPrincipalFor(principal, workspace)}
        />
      );

    // The FIRM's preferences on a firm workspace, exactly as the inbox above is
    // the firm's inbox — and writable only by somebody who administers it,
    // because choosing how an organisation is told things is an act of the
    // organisation. `ondo.set_notification_preference()` refuses it either way;
    // this is what stops the screen offering a control that would be refused.
    case "notification-preferences":
      return (
        <NotificationPreferences
          principalOndoId={inboxPrincipalFor(principal, workspace)}
          canWrite={atLeast(membership?.level, "admin")}
          isFirm={workspace.organisation !== null}
        />
      );

    case "about":
      return <About />;

    // The PERSON's own ONDO id, even in the gallery's workspace, so their acts
    // read "You" rather than their name. The opposite of the inbox below it and
    // deliberately so: an inbox is addressed to a principal, a trail is read by
    // a reader, and the reader is always the person signing in.
    case "activity":
      // `administers` CHANGES NO ROWS. The trail's contents are decided by
      // `access_events_read` — a viewer's own reads, an administrator's whole
      // firm — and this only picks which sentence an EMPTY trail gets, so an
      // ordinary seat is never told the firm has opened nothing when the truth
      // is that they have. `atLeast` is the same derivation the rest of this
      // file uses for a rung; no new capability was invented for it.
      return (
        <Activity
          workBase={view.hrefs.workBase}
          viewerOndoId={principal.ondoId}
          administers={atLeast(membership?.level, "admin")}
        />
      );

    // Scoped to the GALLERY on an organisation's workspace — see
    // `inboxPrincipalFor`, which is where the argument is. A gallery workspace is
    // always an organisation's, so this resolves to the firm every time; the
    // fallback is kept because the function is shared and must stay total.
    // A GALLERY BORROWS TOO, which is why this is here and not only on the
    // collector's portal. Borrowing a work for a show is not consigning it to
    // sell: a consignment is possession in order to SELL and a loan is
    // possession full stop, and the two reach a gallery through different rows.
    case "borrowed":
      return <BorrowedLoans workBase={view.hrefs.workBase} />;

    case "verifications":
      return <Verifications principalOndoId={inboxPrincipalFor(principal, workspace)} />;

    // Takes the workspace rather than the principal, because WHOSE profile this
    // is follows from whether the workspace has an organisation.
    case "profile":
      return <Profile workspace={workspace} onSignOut={onSignOut} />;

    // `record` is non-null here by construction: `resolveWorkspaceRoute` sends a
    // work destination without one back to the inventory.
    case "work":
      return record ? (
        <WorkRecord
          workId={record}
          // A gallery holds no collection — a consignment reaches the work and
          // stops — so "back to the collection" is not a place it has. The
          // inventory is.
          collectionHref={view.hrefs.recordFallback}
          // A gallery owns no work it holds on consignment, so the server says
          // false and the panel hides itself. Passed rather than hard-coded,
          // because the answer is the server's: a director may own work of their
          // own through the same account.
          canAssignAgent={capabilities.assignAgent}
          canConsign={capabilities.consignWork}
          canWriteHistory={atLeast(membership?.level, "editor")}
          // OND-68. The firm this read is recorded against — the same value
          // Contacts passes, from the one place that knows which seat is open.
          // A gallery workspace is always an organisation's, so this is a firm
          // every time; the fallback keeps the expression total.
          actingAs={workspace.organisation?.ondoId ?? null}
        />
      ) : null;

    // The last of the three the sale produces, and the last destination on
    // this rail to get a table. Admin records payment — the settle_resale()
    // rung — because it commits the firm's word.
    case "transactions":
      return (
        <TransactionCentre
          workBase={view.hrefs.workBase}
          soldHref={workspacePath(workspace, "sold")}
          settlementsHref={workspacePath(workspace, "settlements")}
          canRecord={atLeast(membership?.level, "admin")}
          firmOndoId={workspace.organisation?.ondoId ?? principal.ondoId}
        />
      );

    // THE FIRM'S POSITION, and it takes no rung at all — OND-97.
    //
    // Every other money screen on this rail passes a membership level down,
    // because each of them offers a control the database requires `admin` for:
    // `settle_resale`, `answer_settlement`, `unlock_covenant`,
    // `record_registration_fee_paid`. This one offers none. It reads
    // `ondo.gallery_registration_fees()` and `ondo.gallery_settlements()`, both
    // of which admit a `viewer`, and it has no write path to decline — so
    // passing a rung here would be a check with nothing behind it, and the
    // policies on those two functions are the whole of the access control.
    //
    // A cataloguer may therefore read what the firm owes and is owed. That is
    // the same disclosure the Resale settlements screen already makes to the
    // same seat; what it still cannot do is record that any of it has moved.
    case "reporting":
      return <Reporting workBase={view.hrefs.workBase} />;

    // Nothing is `planned` on this rail any more. The default renders the
    // spec's `notYet` copy so that the next planned destination — if there is
    // one — is a page naming what it waits on rather than a blank.
    default:
      return <NotBuiltYet copy={view.notYet} />;
  }
}
